import { useRef, useState, useCallback, useEffect } from 'react';

type SlideToDeclineProps = {
  /** Fired once when the thumb crosses the completion threshold (released at ≥ threshold). */
  onDecline: () => void;
  /** Disable interaction (e.g. while a request is in flight). */
  disabled?: boolean;
  /** Theme tokens for coloring. */
  trackBackground: string;
  thumbColor: string;
  labelColor: string;
  /** Track label text. */
  label?: string;
  /** Fraction of track width the thumb must reach to confirm (0–1). */
  threshold?: number;
};

/**
 * Slide-to-decline slider. The user must drag the thumb from the left edge
 * fully across the track (default 85% of usable width) to confirm a decline.
 * Releasing early snaps the thumb back to the start. Only the completed
 * drag fires `onDecline` — identical to the prior tap-button behavior, just
 * with a deliberate gesture to prevent accidental declines.
 *
 * Uses pointer events (works for touch + mouse), clamps movement to the
 * track, and stops propagation so dragging never triggers the card's
 * tap-to-accept handler.
 */
export default function SlideToDecline({
  onDecline,
  disabled = false,
  trackBackground,
  thumbColor,
  labelColor,
  label = 'Slide to reject',
  threshold = 0.85,
}: SlideToDeclineProps) {
  const trackRef = useRef<HTMLDivElement | null>(null);
  // Thumb width is fixed via CSS; we read it after mount for clamping.
  const thumbWidthRef = useRef(56);
  const [progress, setProgress] = useState(0); // 0..1 of usable travel
  const draggingRef = useRef(false);
  const [dragging, setDragging] = useState(false);
  const [fired, setFired] = useState(false);

  // Measure thumb width once mounted so the clamp math is exact.
  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;
    const thumbEl = track.querySelector<HTMLElement>('[data-thumb]');
    if (thumbEl) thumbWidthRef.current = thumbEl.offsetWidth;
  }, []);

  const maxTravel = useCallback(() => {
    const track = trackRef.current;
    if (!track) return 0;
    return Math.max(0, track.clientWidth - thumbWidthRef.current - 8); // 8px = padding (4px each side)
  }, []);

  const setThumbFromClientX = useCallback((clientX: number) => {
    const track = trackRef.current;
    if (!track) return;
    const rect = track.getBoundingClientRect();
    const travel = maxTravel();
    if (travel <= 0) return;
    const x = clientX - rect.left - 4; // 4px left padding
    const clamped = Math.max(0, Math.min(x, travel));
    setProgress(clamped / travel);
  }, [maxTravel]);

  const onPointerDown = useCallback((e: React.PointerEvent) => {
    if (disabled || fired) return;
    e.stopPropagation();
    (e.target as HTMLElement).setPointerCapture?.(e.pointerId);
    draggingRef.current = true;
    setDragging(true);
    setThumbFromClientX(e.clientX);
  }, [disabled, fired, setThumbFromClientX]);

  const onPointerMove = useCallback((e: React.PointerEvent) => {
    if (!draggingRef.current || disabled) return;
    e.stopPropagation();
    setThumbFromClientX(e.clientX);
  }, [disabled, setThumbFromClientX]);

  const finishDrag = useCallback((e?: React.PointerEvent) => {
    if (!draggingRef.current) return;
    if (e) e.stopPropagation();
    draggingRef.current = false;
    setDragging(false);
    setProgress((p) => {
      if (p >= threshold && !fired) {
        setFired(true);
        // Defer so the thumb visibly reaches the end before the overlay closes.
        setTimeout(() => onDecline(), 60);
        return p;
      }
      // Snap back.
      return 0;
    });
  }, [threshold, fired, onDecline]);

  const translateX = progress * maxTravel();
  const reachedThreshold = progress >= threshold;

  return (
    <div
      ref={trackRef}
      data-slider-track
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={finishDrag}
      onPointerCancel={finishDrag}
      onClick={(e) => e.stopPropagation()}
      className="relative w-full h-12 rounded-full overflow-hidden select-none touch-none disabled:opacity-60"
      style={{ background: trackBackground, opacity: disabled ? 0.6 : 1, cursor: disabled ? 'not-allowed' : 'pointer' }}
    >
      {/* Track label (fades as the thumb covers it) */}
      <div
        className="absolute inset-0 flex items-center justify-end pr-5 pointer-events-none transition-opacity duration-150"
        style={{ opacity: reachedThreshold ? 0 : Math.max(0, 1 - progress * 1.4) }}
      >
        <span className="text-sm font-semibold whitespace-nowrap" style={{ color: labelColor, opacity: 0.85 }}>
          {label}
        </span>
      </div>

      {/* Filled portion behind the thumb */}
      <div
        className="absolute top-1 bottom-1 left-1 rounded-full pointer-events-none"
        style={{
          width: `calc(${(progress * 100).toFixed(2)}% )`,
          background: thumbColor,
          opacity: 0.16,
        }}
      />

      {/* Thumb / handle */}
      <div
        data-thumb
        className="absolute top-1 left-1 h-10 w-14 rounded-full flex items-center justify-center shadow-md pointer-events-none"
        style={{
          background: thumbColor,
          transform: `translateX(${translateX}px)`,
          transition: dragging ? 'none' : 'transform 220ms cubic-bezier(0.22, 1, 0.36, 1)',
        }}
      >
        <span className="text-white text-lg font-bold leading-none" style={{ opacity: reachedThreshold ? 0.9 : 1 }}>
          {reachedThreshold ? '✓' : '←'}
        </span>
      </div>
    </div>
  );
}
